import {GOOGLE_MAPS_API_KEY} from '../config/apis';
import {CITY_SEARCH_COUNTRIES, POPULAR_CITIES} from '../config/onboardingData';

// City search, city coordinates and "which city is this GPS point in?",
// all via Google Maps Platform.
// TODO(API): move these calls behind our backend so the key isn't in the app.
const GOOGLE = 'https://maps.googleapis.com/maps/api';

const getJson = async url => {
  const res = await fetch(`${url}&key=${GOOGLE_MAPS_API_KEY}`);
  const json = await res.json();
  // ZERO_RESULTS is a normal "nothing found", anything else is a failure
  if (json.status !== 'OK' && json.status !== 'ZERO_RESULTS') {
    throw new Error(json.error_message || json.status);
  }
  return json;
};

// City shape used by the screens:
//   {id, name, region, stationsActive?, latitude?, longitude?}
// Results are ranked Netherlands-first; for Dutch results Google leaves the
// country out, so `region` can be ''.
// Google results have no coordinates yet: getCityCoordinates fills them in.
//
// Fewer than 2 characters: the popular list (filtered locally).
// Starts with a digit: postal code search (e.g. "1011" -> Amsterdam).
export const searchCities = async (query, language = 'en') => {
  const q = (query || '').trim();
  if (q.length < 2) {
    const lower = q.toLowerCase();
    return POPULAR_CITIES.filter(c => c.name.toLowerCase().startsWith(lower));
  }

  const types = /^\d/.test(q) ? '(regions)' : '(cities)';
  const components = CITY_SEARCH_COUNTRIES.map(c => `country:${c}`).join('|');
  const json = await getJson(
    `${GOOGLE}/place/autocomplete/json?input=${encodeURIComponent(q)}` +
      `&types=${encodeURIComponent(types)}&components=${components}` +
      `&language=${language}&region=${CITY_SEARCH_COUNTRIES[0]}`,
  );
  return json.predictions.map(p => {
    const main = p.structured_formatting?.main_text || p.description;
    const secondary = p.structured_formatting?.secondary_text || '';
    if (p.types?.includes('postal_code')) {
      // "1011" / "Amsterdam, Netherlands" -> "Amsterdam" / "1011 • Netherlands"
      const [city, ...rest] = secondary.split(', ');
      return {
        id: p.place_id,
        name: city || main,
        region: [main, ...rest].join(' • '),
      };
    }
    return {id: p.place_id, name: main, region: secondary};
  });
};

// Resolves to the city with latitude/longitude filled in.
export const getCityCoordinates = async city => {
  if (city.latitude != null) return city;
  const json = await getJson(
    `${GOOGLE}/place/details/json?place_id=${encodeURIComponent(city.id)}&fields=geometry`,
  );
  const loc = json.result?.geometry?.location;
  if (!loc) throw new Error('No coordinates for this city');
  return {...city, latitude: loc.lat, longitude: loc.lng};
};

// GPS point -> {name, region}, e.g. {name: 'Amsterdam', region: 'North Holland'}.
// Resolves to null when Google doesn't know a city there.
export const reverseGeocodeCity = async ({latitude, longitude}, language = 'en') => {
  const json = await getJson(
    `${GOOGLE}/geocode/json?latlng=${latitude},${longitude}` +
      `&result_type=locality|postal_town|administrative_area_level_2&language=${language}`,
  );
  const parts = json.results?.[0]?.address_components || [];
  const find = type => parts.find(c => c.types.includes(type))?.long_name;
  const name =
    find('locality') || find('postal_town') || find('administrative_area_level_2');
  if (!name) return null;
  return {name, region: find('administrative_area_level_1') || ''};
};
// GPS point -> {city, country, pincode} (any of them can be null).
export const reverseGeocodeDetails = async ({latitude, longitude}, language = 'en') => {
  const json = await getJson(
    `${GOOGLE}/geocode/json?latlng=${latitude},${longitude}&language=${language}`,
  );
  const all = (json.results || []).flatMap(r => r.address_components || []);
  const find = type => all.find(c => c.types.includes(type))?.long_name;
  return {
    city:
      find('locality') ||
      find('postal_town') ||
      find('administrative_area_level_2') ||
      null,
    country: find('country') || null,
    pincode: find('postal_code') || null,
  };
};